import { UseChatHelpers, useChat as useChatSdk } from '@ai-sdk/react';
import { useQuery } from '@apollo/client';
import { AgentId } from '@procureai/agents';
import { DefaultChatTransport } from 'ai';
import { useEffect } from 'react';
import { Union } from 'ts-toolbelt';
import { v7 as uuidv7 } from 'uuid';

import { INTAKE_CHAT_THREAD_DETAILS } from '../IntakeChatThreadDetails';
import { SvbrgAgentId } from '@/generated/gql/graphql';
import { UIChatMessage } from '@/legacy/components/Chat/chat.types';
import { appConfig } from '~/common/config';
import { useChatHistoryHydration } from '~/hooks/useChatHistoryHydration/useChatHistoryHydration';
import { fetchClient } from '~/services/http';

/**
 * Response object returned by the useChat hook.
 *
 * @interface UseChatResponse
 * @property {boolean} isLoading - Indicates whether the chat is currently loading or processing * @property {UseChatHelpers} [chat] - Optional chat helpers object containing methods and utilities for chat operations
 * @property {SvbrgAgentId} [agentId] - Optional identifier for the associated agent handling the chat
 */
export interface UseChatResponse {
  isLoading: boolean;
  chat?: UseChatHelpers<UIChatMessage>;
  agentId?: SvbrgAgentId;
}

/**
 * Custom hook for managing chat functionality with thread support.
 *
 * This hook handles the initialization and management of a chat session, including:
 * - Loading existing thread details from the server
 * - Determining the appropriate agent ID for the conversation
 * - Setting up authentication headers for API requests
 * - Configuring the chat SDK with proper request body formatting
 * - Managing message state synchronization between server and client
 *
 * @param threadId - The ID of an existing thread to load, or null/undefined for a new chat
 * @returns An object containing:
 *   - `isLoading`: Boolean indicating if thread data is being fetched
 *   - `chat`: The initialized chat SDK instance (undefined if not ready)
 *   - `agentId`: The ID of the agent handling the conversation (undefined if not ready)
 *
 * @example
 * ```typescript
 * const { isLoading, chat, agentId } = useChat(threadId);
 *
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (chat) {
 *   // Chat is ready to use
 *   chat.append({ role: 'user', content: 'Hello!' });
 * }
 * ```
 */
export function useChat(threadId: Union.Nullable<string>): UseChatResponse {
  // load thread details from the server
  const { loading, data } = useQuery(INTAKE_CHAT_THREAD_DETAILS, {
    variables: { id: threadId },
    skip: !threadId,
    fetchPolicy: 'cache-and-network',
  });

  // grab thread from response
  const thread = data?.sevenbridges.thread;
  // We use the retrieved thread ID to (re-)initialize the useChatSdk hook to ensure we
  // actually switch from initialized `frontdeskAgent` (as query result is undefined at first)
  // to the real thread ID once loaded and leverage this for their internal memoization
  const retrievedThreadId = thread?.id;

  // Determine if we should initialize the chat
  // For new threads (no threadId): initialize immediately
  // For existing threads: wait until query completes
  const shouldInitialize = !threadId || (!loading && !!data);

  // get the agentId from the current thread info (with fallback to frontdeskAgent)
  // Always compute the agentId (even when not initializing) so we can use it in fallback config
  const agentId: SvbrgAgentId = thread?.metadata?.agentId ?? AgentId.frontdeskAgent;

  // Always call the hook to satisfy React rules
  const internalChat = useChatSdk<UIChatMessage>({
    id: retrievedThreadId,
    generateId: uuidv7,
    transport: new DefaultChatTransport({
      api: `${appConfig.sevenbridges.url}/api/chat/${agentId}`,
      fetch: fetchClient,
      prepareSendMessagesRequest: async (request) => {
        return {
          body: {
            messages: [request.messages.at(-1)],
            threadId,
          },
        };
      },
    }),
  });

  const { setMessages } = internalChat;

  // effect to set loaded messages to the chat SDK
  useEffect(() => {
    if (!shouldInitialize || threadId) return;

    setMessages([]);
  }, [shouldInitialize, threadId, setMessages]);

  useChatHistoryHydration({
    sessionKey: retrievedThreadId ?? threadId ?? 'no-thread',
    enabled: shouldInitialize,
    history: thread?.messages as UIChatMessage[] | undefined,
    messages: internalChat.messages,
    setMessages,
  });

  // Only return the chat object when properly initialized
  return {
    isLoading: loading,
    chat: shouldInitialize ? internalChat : undefined,
    agentId: shouldInitialize ? agentId : undefined,
  };
}
